import { Component, Inject, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  CommonService,
  CONSTANT,
  FileUploadService,
  LoaderService,
  ObservableService,
  OrderService,
} from '@bst-frontend/shared';
import { finalize } from 'rxjs';

export type ReadyDispatchMode = 'create' | 'edit' | 'view';

export interface Product {
  productId: string;
  code: string;
  name: string;
}

export interface RequestedItem {
  product: Product;
  qty: number;
  weight: number;
  subItem?: any;
}

export interface ProductDifferencesDto {
  requested: RequestedItem[];
  // loaded / dispatched / delivered can exist but are not needed here
}

export interface UploadedFile {
  id: string; // use fileKey from S3
  name: string;
  url: string; // public URL
  mimeType: string;
  size?: number;
}

export interface DifferencePayloadItem {
  product: Product;
  subItem: Product;
  changeInSubItem: Product;
  qty: number;
  changeInQty: number;
  weight: number;
  changeInWeight: number;
}

export interface FilePayload {
  path: string;
  name: string;
  id: string;
  size?: number;
}

export interface ReadyDispatchPayload {
  differences: DifferencePayloadItem[];
  loadingImages: FilePayload[];
  companyInvoice: FilePayload[];
  weightSlips: FilePayload[];
  calculateData: any;
  // invoiceNumber?: string;
  // paymentMode?: string;
  // eWayBillNo?: string;
}

/**
 * Optional: for edit/view we can receive backend payload to pre-fill
 */

@Component({
  selector: 'bst-frontend-ready-dispatch-modal',
  templateUrl: './ready-dispatch-modal.component.html',
  styleUrls: ['./ready-dispatch-modal.component.css'],
})
export class ReadyDispatchModalComponent implements OnInit {
  /** create | edit | view */
  @Input() mode: ReadyDispatchMode = 'create';

  @Input() orderData: any;
  @Input() subOrder?: any;
  @Input() config: any;

  /** Product differences coming from parent */
  @Input() productDifferences!: ProductDifferencesDto;

  /** Optional existing payload for edit/view */
  @Input() initialData?: any;

  dispatchForm!: FormGroup;

  // upload state
  loadingFiles: UploadedFile[] = [];
  weightSlipFiles: UploadedFile[] = [];
  invoiceFiles: UploadedFile[] = [];

  // dropdown options for pickup product
  productOptions: Product[] = [];

  isSubmitting = false;

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private fileUploadService: FileUploadService,
    private loader: LoaderService,
    private orderService: OrderService,
    private observableService: ObservableService,
    public commonService: CommonService,
    @Inject('environment') private environment: any
  ) {}

  ngOnInit(): void {
    if (!this.productDifferences || !this.productDifferences.requested) {
      throw new Error('productDifferences.requested is required');
    }

    this.productOptions = this.productDifferences.requested.map(
      (r) => r.product
    );

    this.buildForm();
    this.buildItemsFromRequested();
    this.initFilesFromInitialData();

    if (this.mode === 'view') {
      this.dispatchForm.disable({ emitEvent: false });
    }
  }

  // ---------- FORM BUILDING ----------

  private buildForm(): void {
    this.dispatchForm = this.fb.group({
      items: this.fb.array([]),

      // keep these if you need LR details, can be wired to template later
      // invoiceNumber: [this.subOrder?.invoiceNumber, Validators.required],
      // paymentMode: [this.subOrder?.paymentMode, Validators.required],
      // eWayBillNo: [this.subOrder?.eWayBillNo, Validators.required],
    });
  }

  get items(): FormArray {
    return this.dispatchForm.get('items') as FormArray;
  }

  private buildItemsFromRequested(): void {
    const requested = this.productDifferences.requested;

    requested.forEach((req) => {
      const existingDiff = this.findExistingDifference(req.product, req.subItem);
      const requestedQty = req.qty;
      const requestedWeight = req.weight;

      // pickup (what user can modify)
      const pickupQty = existingDiff != null ? existingDiff.qty : req.qty;
      const pickupWeight =
        existingDiff != null ? existingDiff.weight : req.weight;
      const pickupProduct =
        existingDiff != null ? existingDiff.subItem : req.subItem;

      this.items.push(
        this.fb.group({
          product: [req.product], // original product
          requestedQty: [requestedQty],
          requestedWeight: [requestedWeight],
          subItem: [req.subItem],

          pickupProduct: [pickupProduct],
          pickupQty: [pickupQty, [Validators.required]],
          pickupWeight: [pickupWeight, [Validators.required]],
        })
      );
    });
  }

  private findExistingDifference(
    product: Product,
    subItem: any
  ): DifferencePayloadItem | undefined {
    if (!this.initialData || !this.initialData?.dispatched?.length) {
      return undefined;
    }
    if(subItem){
      return this.initialData?.dispatched.find(
        (d: any) => d.subItem.productId == subItem.productId
      );
    } else {
      return this.initialData?.dispatched.find(
        (d: any) => d.product.productId == product.productId
      );
    }
  }

  // ---------- FILES INIT (EDIT / VIEW) ----------

  private initFilesFromInitialData(): void {
    if (!this.subOrder) return;

    const mapInputFiles = (input?: FilePayload[]): UploadedFile[] =>
      (input || []).map((f) => ({
        id: f.path,
        name: f.name,
        url: this.environment.s3UrlDownload + f.path,
        path: f.path,
        mimeType: '', // you can’t know this from backend unless it sends
        size: f.size,
      }));

    this.loadingFiles = mapInputFiles(this.subOrder?.loadingImages);
    this.weightSlipFiles = mapInputFiles(this.subOrder?.weightSlips);
    this.invoiceFiles = mapInputFiles(this.subOrder?.companyInvoice);
  }

  // ---------- TEMPLATE HELPERS ----------

  compareProducts(a: Product | null, b: Product | null): boolean {
    return !!a && !!b && a.productId === b.productId;
  }

  isImage(file: UploadedFile): boolean {
    const mime = (file.mimeType || '').toLowerCase();

    // if mimeType is set and says image → it's an image
    if (mime.startsWith('image/')) {
      return true;
    }

    // fallback: check extension in url or name
    const nameOrUrl = (file.url || file.name || '').toLowerCase();
    return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(nameOrUrl);
  }

  // ---------- FILE UPLOAD / REMOVE / PREVIEW ----------

  onFileSelected(event: Event, type: 'loading' | 'weightSlip' | 'invoice') {
    if (this.mode === 'view') return;

    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const files = Array.from(input.files);
    this.loader.show();
    files.forEach((file) => {
      const documentType = this.getDocumentType(type);

      this.fileUploadService
        .uploadFileToS3(file, file.name, documentType, this.orderData._id) // ← generic upload from earlier
        .pipe(
          finalize(() => {
            this.loader.hide();
          })
        )
        .subscribe({
          next: (info: any) => {
            console.log('info::', info);
            const uploaded: UploadedFile = {
              id: info.fileKey,
              name: file.name,
              url: info.fileUrl,
              mimeType: file.type,
              size: file.size,
            };

            if (type === 'loading') {
              this.loadingFiles.push(uploaded);
            } else if (type === 'weightSlip') {
              this.weightSlipFiles.push(uploaded);
            } else {
              this.invoiceFiles.push(uploaded);
            }
          },
          error: (err) => {
            console.error('Upload failed', err);
          },
        });
    });

    input.value = '';
  }

  private getDocumentType(type: 'loading' | 'weightSlip' | 'invoice'): string {
    switch (type) {
      case 'loading':
        return 'loading_image';
      case 'weightSlip':
        return 'weight_slip';
      case 'invoice':
        return 'company_invoice';
      default:
        return 'general_document';
    }
  }

  onRemoveFile(
    type: 'loading' | 'weightSlip' | 'invoice',
    file: UploadedFile
  ): void {
    if (this.mode === 'view') return;

    if (type === 'loading') {
      this.loadingFiles = this.loadingFiles.filter((f) => f.id !== file.id);
    } else if (type === 'weightSlip') {
      this.weightSlipFiles = this.weightSlipFiles.filter(
        (f) => f.id !== file.id
      );
    } else {
      this.invoiceFiles = this.invoiceFiles.filter((f) => f.id !== file.id);
    }
    // if you later implement direct S3 delete, call it here
  }

  onPreviewFile(file: UploadedFile): void {
    window.open(file.url, '_blank');
  }

  // ---------- PAYLOAD MAPPERS ----------

  private buildDifferences(): DifferencePayloadItem[] {
    return this.items.controls.map((ctrl) => {
      const v = ctrl.value;

      const product: Product = v.product;
      const subItem: Product = v.pickupProduct;

      const qty: number = v.pickupQty;
      const pickupQty: number = v.requestedQty;

      const weight: number = v.pickupWeight;
      const pickupWeight: number = v.requestedWeight;

      return {
        product,
        subItem,
        changeInSubItem: subItem, // if BE expects something else, change here

        qty,
        changeInQty: qty - pickupQty,

        weight,
        changeInWeight: weight - pickupWeight,
      };
    });
  }

  private mapFilesForPayload(files: UploadedFile[]): FilePayload[] {
    return files.map((f) => ({
      id: f.url,
      path: f.id,
      name: f.name,
      size: f.size,
    }));
  }

  // ---------- MODAL ACTIONS ----------

  cancel(): void {
    this.activeModal.dismiss('cancel');
  }

  async onSubmit() {
    let subOrders = [];
    subOrders = JSON.parse(JSON.stringify(this.orderData.subOrders));
    subOrders.forEach((so: any) => {
      so.pickup?.items?.forEach((item: any) => {
      let tempItem: any = null;
      if(item?.product?.subItems?.productId) {
        tempItem = this.items.value.find((i: any) => i?.subItem?.productId === item?.product?.subItems?.productId);
      } else {
        tempItem = this.items.value.find((i: any) => i.product.productId === item.product.productId);
      }
        if (tempItem) {
          item.planned.qty =
            tempItem.pickupQty >= item.planned?.qty
              ? tempItem.pickupQty
              : item.planned?.qty || 0;
          item.planned.weight =
            tempItem.pickupWeight >= item.planned?.weight
              ? tempItem.pickupWeight
              : item.planned?.weight || 0;
        } else {
          item.qty = item.planned?.qty || 0;
          item.weight = item.planned?.weight || 0;
        }
      });
    });
    let calculateData: any = undefined;
    if(this.orderData.type !== 'full-load')
    {
      calculateData = this.commonService.calculateFareFromSubOrders(
        subOrders,
        this.orderData.type,
        this.config
      );

    }
       
    if (this.mode === 'view') {
      this.activeModal.close('view-close');
      return;
    }

    if (this.dispatchForm.invalid) {
      this.dispatchForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;

    const payload: ReadyDispatchPayload = {
      differences: this.buildDifferences(),
      loadingImages: this.mapFilesForPayload(this.loadingFiles),
      companyInvoice: this.mapFilesForPayload(this.invoiceFiles),
      weightSlips: this.mapFilesForPayload(this.weightSlipFiles),
      calculateData,
      // invoiceNumber: this.dispatchForm.value.invoiceNumber,
      // paymentMode: this.dispatchForm.value.paymentMode,
      // eWayBillNo: this.dispatchForm.value.eWayBillNo,
    };

    this.loader.show();
    try {
      await this.orderService
        .updateSubOrder(
          CONSTANT.orderStatus['READY-TO-DISPATCH'],
          this.subOrder._id,
          payload
        )
        .toPromise();
      this.observableService.send({ refresh: true });
      // this.loader.hide();
    } catch (error) {
      console.log('Error', error);
    }
    this.activeModal.close({ status: 'saved' });
    this.isSubmitting = false;
  }
}
